import BaseService from "@/services/BaseService.ts";
import {supabase} from "@supabaseClient";
import {Json} from "@supabaseTypes";
import {SaccoDeposit} from "@/@types/sacco.models.ts";
import {convertTransactionAmounts} from "@lib/currencyConversion/currencyConversion.ts";
import {fetchRatesWithRetry} from "@/integrations/api/currency";
import {buildDepositDescription} from "@saccoUtils";
import {convertLinkedSaccoAmount} from "./linkedAmount.ts";

/**
 * SaccoService class provides methods to interact with and manage sacco-related functionalities.
 * It extends the BaseService class, inheriting its base functionalities and augmenting them
 * with sacco-specific operations. This service can be used to handle operations such as
 * managing members, contributions, loans, and other sacco-related features.
 *
 * Methods in this class are designed to facilitate seamless integration with the sacco system,
 * providing a robust API for external applications or modules to engage with.
 *
 * This class expects to be used in conjunction with a properly initialized instance of BaseService,
 * ensuring that all base functionalities are available.
 *
 * Note: Ensure proper error handling and authentication mechanisms are implemented when invoking
 * methods of this service in production environments.
 */
export class SaccoService extends BaseService {

    /**
     * Creates a new Sacco deposit record in the database.
     *
     * @param {SaccoDepositRequestDto} request - The DTO containing the deposit details to be created.
     * @return {Promise<SaccoDeposit>} A promise that resolves to the created SaccoDeposit object.
     *
     * @deprecated Use {createDepositWithDebit} instead
     */
    public async createDeposit(request: SaccoDepositRequestDto): Promise<SaccoDeposit> {
        try {
            const payload = this.convertRequestToSnakeCase(request);
            const { data, error } = await supabase
                .from('sacco_deposits')
                .insert({ ...payload })
                .select()
                .single();
            if (error) throw error;
            return data;
        } catch (error) {
            console.error("Error creating Sacco deposit", error);
            await this.handleError(error);
        }
    }

    /**
     * Creates a deposit in a SACCO (Savings and Credit Cooperative Organization) using funds from a debit account.
     *
     * @param {string} userId - The unique identifier of the user initiating the deposit.
     * @param {SaccoDepositWithDebitRequest} request - The request object containing details about the deposit,
     * including the source account, deposit amount, currency, membership ID, note, and deposit date.
     * @return {Promise<SaccoDeposit>} A promise that resolves to the created Sacco deposit object.
     * @throws {Error} If the source account is not found, if the deposit creation fails, or if no data is returned from
     * the operation.
     */
    public async createDepositWithDebit(userId: string, request: SaccoDepositWithDebitRequest): Promise<Json> {
        // Fetch source account native currency
        // TODO: move this to AccountService to retrieve account details
        const { data: account, error: accountError } = await supabase
            .from('accounts')
            .select('id, currency')
            .eq('id', request.sourceAccountId)
            .eq('user_id', userId)
            .single();

        if (accountError || !account) {
            await this.handleError(Error(`Source account ${request.sourceAccountId} not found with error ${accountError}`));
            throw new Error(`Source account ${request.sourceAccountId} not found`);
        }

        try {
            const nativeCurrency = account.currency;

            // Determine debit amount — skip fetchRatesWithRetry when currencies match
            let debitAmount: number;
            if (request.depositCurrency === nativeCurrency) {
                debitAmount = request.amount;
            } else {
                const result = await convertTransactionAmounts(
                    {
                        amount: request.amount,
                        fee: 0,
                        fromCurrency: request.depositCurrency,
                        toCurrency: nativeCurrency,
                    },
                    fetchRatesWithRetry
                );
                debitAmount = result.convertedAmount;
            }

            const description = buildDepositDescription(request.note);

            const { data, error } = await supabase.rpc('create_sacco_deposit_with_debit', {
                p_user_id: userId,
                p_membership_id: request.membershipId,
                p_source_account_id: request.sourceAccountId,
                p_entered_amount: request.amount,
                p_debit_amount: debitAmount,
                p_deposit_currency: request.depositCurrency,
                p_native_currency: nativeCurrency,
                p_deposit_date: request.depositDate,
                p_description: description,
            });

            if (error) {
                throw new Error(error.message || 'Failed to create sacco deposit');
            }

            if (!data) {
                throw new Error('No data returned from sacco deposit creation');
            }

            return data;
        } catch (error) {
            console.error("Error creating Sacco deposit with debit", error);
            await this.handleError(error);
        }
    }

    /**
     * Fetches Sacco deposits for the user given their membership ID
     * @param {string} userId the user's ID
     * @param {string} membershipId the user's sacco membership ID
     */
    public async fetchDeposits(
        userId: string,
        membershipId: string,
    ): Promise<SaccoDeposit[]> {
        try {
            const { data, error } = await supabase
                .from('sacco_deposits')
                .select('*')
                .eq('membership_id', membershipId)
                .eq('user_id', userId)
                .order('deposit_date', { ascending: true });
            if (error) throw error;
            return data;
        } catch (error) {
            console.error("Error fetching Sacco deposits", error);
            await this.handleError(error);
        }
    }

    /**
     * Updates an existing Sacco deposit wuth a debitin the database.
     * Converts the provided request to snake_case format and sends an update operation to the database.
     *
     * @param {SaccoDepositWithDebitUpdateRequest} request - The request object containing the deposit details to be updated.
     * Must include an `id` property for identifying the record.
     * @return {Promise<SaccoDeposit>} A promise that resolves to the updated Sacco deposit object.
     * @throws Will throw an error if the update operation fails.
     */
    public async updateDeposit(request: SaccoDepositUpdateRequest): Promise<SaccoDeposit> {
        try {
            const payload = this.convertRequestToSnakeCase(request);
            const { data, error } = await supabase
                .from('sacco_deposits')
                .update(payload)
                .eq('id', request.id)
                .select()
                .single();
            if (error) throw error;
            return data;
        } catch (error) {
            console.error("Error updating Sacco deposit", error);
            await this.handleError(error);
        }
    }

    /**
     * Updates a Sacco deposit. A deposit funded by a debit has its linked transaction re-priced and the
     * source account balance adjusted in the same database transaction.
     *
     * @param {string} userId - The user who owns the deposit.
     * @param {SaccoDepositWithDebitUpdateRequest} request - The deposit's new amount, date and note.
     * @return {Promise<SaccoDeposit>} The updated deposit.
     */
    public async updateDepositWithDebit(userId: string, request: SaccoDepositWithDebitUpdateRequest): Promise<SaccoDeposit> {
        try {
            const debitAmount = await convertLinkedSaccoAmount(
                userId, 'sacco_deposits', request.depositId, request.amount
            );
            const { data, error } = await supabase.rpc('update_sacco_deposit_with_debit', {
                p_deposit_id: request.depositId,
                p_user_id: userId,
                p_amount: request.amount,
                p_deposit_date: request.depositDate,
                p_note: request.note ?? null,
                p_debit_amount: debitAmount,
            });

            // Rethrow the PostgREST error as is, so Sentry receives its code and details.
            if (error) throw error;

            return data;
        } catch (error) {
            console.error("Error updating Sacco deposit", error);
            await this.handleError(error, { depositId: request.depositId });
        }
    }

    /**
     * Deletes a Sacco deposit. When the deposit was funded by a debit transaction, the transaction is
     * deleted through delete_transaction_with_balance, which restores the funding account balance and
     * removes the deposit in the same database transaction.
     *
     * @param {string} id - The unique identifier of the deposit to be deleted.
     * @param {string} userId - The user who owns the deposit.
     * @return {Promise<void>} A promise that resolves when the deposit is successfully deleted or rejects with an error.
     */
    public async deleteDeposit(id: string, userId: string): Promise<void> {
        try {
            const { data: deposit, error: fetchError } = await supabase
                .from('sacco_deposits')
                .select('transaction_id')
                .eq('id', id)
                .single();
            if (fetchError) throw fetchError;

            if (deposit?.transaction_id) {
                const { error } = await supabase.rpc('delete_transaction_with_balance', {
                    p_transaction_id: deposit.transaction_id,
                    p_user_id: userId,
                });
                if (error) throw error;
                return;
            }

            const { error } = await supabase.from('sacco_deposits').delete().eq('id', id);
            if (error) throw error;
        } catch (error) {
            console.error("Error deleting Sacco deposit", error);
            await this.handleError(error);
        }
    }

}

export default new SaccoService();
